import { cn } from '@mdvip/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const badgeVariants = cva(
  'inline-flex items-center justify-center gap-1 rounded-full border font-medium whitespace-nowrap shrink-0 w-fit transition-colors overflow-hidden [&>svg]:size-3 [&>i]:size-3 [&>svg]:pointer-events-none [&>i]:pointer-events-none focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background outline-none',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'text-foreground border-input',
        muted: 'border-transparent bg-muted text-muted-foreground',
        /* Every semantic badge takes its token's FULL fill. The tokens are already 50-level tints,
           so a further /10–/15 on top of one left no visible background at all. successSolid stays
           as an alias. */
        destructive: 'border-transparent bg-destructive text-destructive-foreground',
        success: 'border-transparent bg-success text-success-foreground',
        successSolid: 'border-transparent bg-success text-success-foreground',
        info: 'border-transparent bg-info text-info-foreground',
        warning: 'border-transparent bg-warning text-warning-foreground',
      },
      /* The pill ladder: the box and the type move together. sm 24px box · 12/16 type ·
         md 32px · 14/20 · lg 40px · 16/24, so the pill is sized by its type rather than by
         padding. A link or filter chip still clears the 44px pressable floor — `size` moves a
         label's box, never a target's. */
      size: {
        sm: 'h-6 px-2 text-xs leading-4',
        md: 'h-8 px-3 text-sm leading-5',
        lg: 'h-10 px-4 text-base leading-6 [&>svg]:size-3.5 [&>i]:size-3.5',
      },
    },
    defaultVariants: { variant: 'default', size: 'md' },
  },
);

/**
 * A tone is a colour FAMILY, not a meaning: it lets a badge carry a theme's hue (a health theme, a
 * category chip) without call sites hand-styling through className. Tone wins over variant when
 * both are given. Every string is written out rather than built at runtime, so Tailwind's scanner
 * can see it.
 *
 * `soft` grounds are per family: tangerine, green, magenta and red take their 500; melon its 200;
 * sand its 100; blue, gold, cyan and neutral keep the 50. The ink is whichever of white or dark
 * grey clears 4.5:1 on that ground — measured: sand 14.04 · melon 11.76 · magenta 7.42 ·
 * tangerine 5.82 · green 4.90 · red 4.47 (red misses by 0.03, flagged upstream).
 * `solid` carries white type, so its fill is the lightest rung of the family on which white clears
 * 4.5:1 — measured, which is why the rungs differ (cyan and melon need 800, tangerine and sand 700,
 * the rest 600).
 */
const BADGE_TONES: Record<BadgeEmphasis, Record<BadgeTone, string>> = {
  soft: {
    tangerine: 'border-[var(--tangerine-600)]/40 bg-[var(--tangerine-500)] text-[var(--neutral-800)]',
    green: 'border-[var(--green-600)]/40 bg-[var(--green-500)] text-white',
    magenta: 'border-[var(--magenta-600)]/40 bg-[var(--magenta-500)] text-white',
    blue: 'border-[var(--blue-500)]/40 bg-[var(--blue-50)] text-[var(--blue-500)]',
    red: 'border-[var(--red-600)]/40 bg-[var(--red-500)] text-white',
    gold: 'border-[var(--gold-500)]/40 bg-[var(--gold-50)] text-[var(--gold-800)]',
    melon: 'border-[var(--melon-400)]/50 bg-[var(--melon-200)] text-[var(--neutral-800)]',
    sand: 'border-[var(--sand-300)]/60 bg-[var(--sand-100)] text-[var(--neutral-800)]',
    cyan: 'border-[var(--cyan-500)]/40 bg-[var(--cyan-50)] text-[var(--cyan-900)]',
    neutral: 'border-[var(--neutral-500)]/40 bg-[var(--neutral-50)] text-[var(--neutral-700)]',
  },
  solid: {
    tangerine: 'border-transparent bg-[var(--tangerine-700)] text-white',
    green: 'border-transparent bg-[var(--green-600)] text-white',
    magenta: 'border-transparent bg-[var(--magenta-600)] text-white',
    blue: 'border-transparent bg-[var(--blue-600)] text-white',
    red: 'border-transparent bg-[var(--red-600)] text-white',
    gold: 'border-transparent bg-[var(--gold-600)] text-white',
    melon: 'border-transparent bg-[var(--melon-800)] text-white',
    sand: 'border-transparent bg-[var(--sand-700)] text-white',
    cyan: 'border-transparent bg-[var(--cyan-800)] text-white',
    neutral: 'border-transparent bg-[var(--neutral-600)] text-white',
  },
};

export type BadgeTone =
  | 'tangerine'
  | 'green'
  | 'magenta'
  | 'blue'
  | 'red'
  | 'gold'
  | 'melon'
  | 'sand'
  | 'cyan'
  | 'neutral';
export type BadgeEmphasis = 'soft' | 'solid';

export interface BadgeProps extends React.ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
  /** Colour family, independent of meaning. Wins over `variant` when both are given. */
  tone?: BadgeTone;
  /** @default 'soft' */
  emphasis?: BadgeEmphasis;
  /**
   * Render as a different element (a link, a button) while keeping the badge styling. The DS twin
   * calls this `asChild`; `render` is this repo's Base UI-aligned spelling of the same thing.
   */
  render?: React.ReactElement<{ className?: string }>;
}

function Badge({ className, variant, tone, emphasis = 'soft', size, render, ...props }: BadgeProps) {
  const toned = tone ? BADGE_TONES[emphasis][tone] : undefined;
  const classes = cn(badgeVariants({ variant: toned ? undefined : variant, size }), toned, className);

  if (React.isValidElement(render)) {
    return React.cloneElement(render, {
      'data-slot': 'badge',
      className: cn(render.props.className, classes),
      ...props,
    } as React.HTMLAttributes<HTMLElement>);
  }
  return <span data-slot="badge" className={classes} {...props} />;
}

export { Badge, badgeVariants };
